import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { FacilityPicker } from "components/FacilityPicker";
import {
  FacilityPickerFragment,
  VolumeStatus,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { StatStrip } from "../components/StatStrip";
import { AccumulationAreaDialog } from "./AccumulationAreaDialog";
import { FACILITY_ACCUMULATION_AREAS_QUERY } from "./api";
import { AreaCard, AreaCardArea } from "./AreaCard";

/** Composes the free-text box and the two filter controls into the keyed
 * search string the `accumulationAreas(search:)` argument parses. */
function buildSearchString(
  freeText: string,
  type: WasteAccumulationAreaType | "",
  overThresholdOnly: boolean
): string {
  const parts = [freeText.trim()];
  if (type) {
    parts.push(`type:${type}`);
  }
  if (overThresholdOnly) {
    parts.push("overThreshold:true");
  }
  return parts.filter(Boolean).join(" ");
}

/** Uppercase section label, a hairline rule running to a right-aligned
 * caption chip (e.g. "Volume-governed · no time clock"). The chip is
 * standard default MUI styling — it's a caption, not a status indicator. */
function AreaSectionHeader({ label, caption }: { label: string; caption: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography
        component="span"
        variant="overline"
        sx={{ whiteSpace: "nowrap", fontWeight: 800, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Divider sx={{ flex: 1 }} />
      <Chip label={caption} size="small" sx={{ flexShrink: 0, fontWeight: 700 }} />
    </Stack>
  );
}

/** Auto-fill card grid: cards at least 20.625rem wide, standard theme gap. */
function AreaGrid({ areas }: { areas: AreaCardArea[] }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(20.625rem, 1fr))",
        gap: 2,
      }}
    >
      {areas.map((area) => (
        <AreaCard key={area.id} area={area} />
      ))}
    </Box>
  );
}

function FacilityScopeChip({ name, onClear }: { name: string; onClear: () => void }) {
  return <Chip label={`Facility: ${name}`} size="small" variant="outlined" onDelete={onClear} />;
}

/** The facility EPA-ID pill sitting beside the facility-name H1. Static (no
 * picker) — renders only when a primary EPA ID is available. */
function EpaIdPill({ epaId }: { epaId: string }) {
  return <Chip label={epaId} size="small" variant="outlined" />;
}

/** Shown when the page is reached without a `?facilityId=` — this page has no
 * tenant-wide mode since the schema only exposes areas per facility. */
function FacilityPickerPrompt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleChange = (facility: FacilityPickerFragment | null) => {
    if (!facility) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("facilityId", facility.id);
    navigate({ search: next.toString() }, { replace: true });
  };

  return (
    <Stack spacing={2} sx={{ px: 3, py: 4, maxWidth: 420 }}>
      <Typography variant="body1">Select a facility to view its accumulation areas.</Typography>
      <FacilityPicker
        label="Facility"
        facilityQueryVariant="waste"
        value={null}
        onChange={handleChange}
        invalid={false}
        isTouched={false}
        isDirty={false}
        isValidating={false}
      />
    </Stack>
  );
}

function FacilityAccumulationAreas({
  tenantId,
  facilityId,
}: {
  tenantId: string;
  facilityId: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [freeText, setFreeText] = useState("");
  const [typeFilter, setTypeFilter] = useState<WasteAccumulationAreaType | "">("");
  const [overThresholdOnly, setOverThresholdOnly] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const search = useMemo(
    () => buildSearchString(freeText, typeFilter, overThresholdOnly),
    [freeText, typeFilter, overThresholdOnly]
  );

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_AREAS_QUERY, {
    variables: { facilityId, search: search || undefined },
    // This page remounts on the way back from an area's detail route (e.g.
    // after archiving), so cache-first would otherwise serve the stale
    // cached list until something else triggers a refetch.
    fetchPolicy: "cache-and-network",
  });

  const items: AreaCardArea[] = data?.facility?.accumulationAreas?.items ?? [];
  const facilityName = data?.facility?.name ?? "Facility";
  const primaryEpaId = data?.facility?.epaIdStatuses?.find(
    (status) => status.isPrimaryForFacility
  )?.epaId;

  const satelliteAreas = items.filter((area) => area.type === WasteAccumulationAreaType.Satellite);
  const centralAreas = items.filter((area) => area.type === WasteAccumulationAreaType.Central);
  const overThresholdCount = items.filter(
    (area) => area.volume.status === VolumeStatus.Over
  ).length;
  const containersOnSite = items.reduce((sum, area) => sum + area.containerCount, 0);

  const clearFacilityScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("facilityId");
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Stack direction="row" sx={{ pt: 1 }}>
        <FacilityScopeChip name={facilityName} onClear={clearFacilityScope} />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={2.25}
        sx={{ pt: 2, pb: 1.75 }}
      >
        <Box>
          <Typography
            component="p"
            variant="overline"
            sx={{ m: 0, mb: 0.5, fontWeight: 700, letterSpacing: ".14em", color: "primary.main" }}
          >
            On-site waste · Accumulation areas
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={1.25} flexWrap="wrap">
            <Typography
              component="h1"
              variant="h5"
              sx={{ m: 0, fontWeight: 700, lineHeight: 1.1, color: "text.primary" }}
            >
              {facilityName}
            </Typography>
            {primaryEpaId && <EpaIdPill epaId={primaryEpaId} />}
          </Stack>
          <Typography component="p" variant="body2" sx={{ m: 0, mt: 0.5, color: "text.secondary" }}>
            Where waste accumulates on site — satellite areas (volume-governed) and central
            accumulation (time-governed)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ExportDataButton
            tenantId={tenantId}
            facilityId={facilityId}
            exportType="accumulationAreas"
            filter={{ search }}
            fileName="accumulation-areas"
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add area
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pb: 2 }}>
        <TextField
          label="Search"
          size="small"
          value={freeText}
          onChange={(event) => setFreeText(event.target.value)}
          sx={{ minWidth: 220 }}
        />
        <Select<WasteAccumulationAreaType | "">
          size="small"
          displayEmpty
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All types</MenuItem>
          <MenuItem value={WasteAccumulationAreaType.Satellite}>Satellite</MenuItem>
          <MenuItem value={WasteAccumulationAreaType.Central}>Central</MenuItem>
        </Select>
        <FormControlLabel
          control={
            <Checkbox
              checked={overThresholdOnly}
              onChange={(event) => setOverThresholdOnly(event.target.checked)}
            />
          }
          label="Over threshold only"
        />
      </Stack>

      <StatStrip
        groups={[
          [{ label: "Areas", value: items.length }],
          [
            { label: "Satellite", value: satelliteAreas.length },
            { label: "Central", value: centralAreas.length },
          ],
          [
            // "Areas", not bare "Over threshold": this counts areas, and it
            // sits beside a container count, where the shorter label reads as
            // though containers were what was over.
            { label: "Areas over threshold", value: overThresholdCount, emphasize: true },
            { label: "Containers on site", value: containersOnSite },
          ],
        ]}
      />

      {!loading && items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
          No accumulation areas are set up for this facility yet.
        </Typography>
      )}

      {satelliteAreas.length > 0 && (
        <Box sx={{ mb: 3.25 }}>
          <Box sx={{ mb: 1.5 }}>
            <AreaSectionHeader
              label="Satellite accumulation"
              caption="Volume-governed · no time clock"
            />
          </Box>
          <AreaGrid areas={satelliteAreas} />
        </Box>
      )}

      {centralAreas.length > 0 && (
        <Box sx={{ mb: 3.25 }}>
          <Box sx={{ mb: 1.5 }}>
            <AreaSectionHeader
              label="Central accumulation"
              caption="Time-governed · by generator status"
            />
          </Box>
          <AreaGrid areas={centralAreas} />
        </Box>
      )}

      {addDialogOpen && (
        <AccumulationAreaDialog
          open
          facilityId={facilityId}
          onClose={() => setAddDialogOpen(false)}
        />
      )}
    </Box>
  );
}

export function AccumulationAreasList() {
  const { tenantId } = useTenant();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Accumulation areas",
    },
  ]);

  if (!tenantId) {
    return null;
  }

  if (!facilityId) {
    return <FacilityPickerPrompt />;
  }

  return <FacilityAccumulationAreas tenantId={tenantId} facilityId={facilityId} />;
}
